import { FC, useCallback, useReducer, useRef } from 'react'

import Placeholder from '@tiptap/extension-placeholder'
import { Underline } from '@tiptap/extension-underline'
import { Fragment, Slice } from '@tiptap/pm/model'
import { useEditor, Extensions } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { TableOfContents } from '@tiptap-pro/extension-table-of-contents'
import { Helmet } from 'react-helmet-async'

import { CustomLink as CustomLinkExtension } from '@bettermode/tiptap-extension-custom-link'
import { Image, SearchEntityType } from '@tribeplatform/gql-client/types'
import { useSearch } from '@tribeplatform/react-sdk/hooks'
import { useDocument } from '@tribeplatform/react-ui-kit/hooks'

import { useStaticIntl } from '../i18n/hooks/useStaticIntl.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'
import { cleanSearchQuery } from '../utils/search.js'

import {
  DialogsAction,
  DialogsState,
  ReducerActionKind,
  ComposerProviderType,
  ComposerContextType,
} from './@types/index.js'
import {
  AttachmentPickerButton,
  AttachmentPickerButtonRef,
} from './components/AttachmentPickerButton.js'
import { ComposerConfigContext } from './ComposerConfigContext.js'
import { ComposerContext } from './ComposerContext.js'
import { DefaultExtensionConfig } from './config/constants.js'
import { Attachment as AttachmentExtension } from './extensions/attachment/index.js'
import { CodeBlock as CodeBlockExtension } from './extensions/code-block/CodeBlock.js'
import { Embed as EmbedExtension } from './extensions/embed/index.js'
import { Emoji as EmojiExtension } from './extensions/emoji/index.js'
import { CustomHeading as CustomHeadingExtension } from './extensions/heading/index.js'
import { AsyncImage as AsyncImageExtension } from './extensions/image/index.js'
import { Mention as MentionExtension } from './extensions/mention/index.js'
import { mention } from './extensions/mention/options.js'
import { PollNode } from './extensions/poll/index.js'
import { slashMenuCommand } from './extensions/slash-menu/click-handler.js'
import { SlashMenu as SlashMenuExtension } from './extensions/slash-menu/index.js'
import {
  slashMenuItemsFactory,
  MenuTypes,
} from './extensions/slash-menu/items.js'
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from './extensions/table/index.js'
import { TableOfContentsNode } from './extensions/table-of-content/index.js'
import { Video as VideoExtension } from './extensions/video/index.js'
import { useAddAttachmentToEditor } from './hooks/useAddAttachmentToEditor.js'
import { useHandleFiles } from './hooks/useHandleFiles.js'
import { useHandlePaste } from './hooks/useHandlePaste.js'
import { useUploadInProgressRemovedListener } from './hooks/useUploadInProgressRemovedListener.js'

function removeNodeFromFragment(fragment: Fragment, nodeTypeName: string) {
  const filteredNodes = []

  fragment.forEach(node => {
    if (node.type.name === nodeTypeName) {
      return
    }

    if (node.content && node.content.size > 0) {
      const filteredContent = removeNodeFromFragment(node.content, nodeTypeName)
      filteredNodes.push(node.copy(filteredContent))
    } else {
      filteredNodes.push(node)
    }
  })

  return Fragment.fromArray(filteredNodes)
}

const initialDialogsState: DialogsState = {
  link: false,
  embed: false,
  video: false,
  audio: false,
  image: false,
  giphy: false,
  embedItem: undefined,
  textFormatMenu: false,
}

function reducer(state: DialogsState, action: DialogsAction) {
  switch (action.type) {
    case ReducerActionKind.SET:
      return { ...state, [action.key]: action.value }
    case ReducerActionKind.SET_EMBED_ITEM:
      return { ...state, embedItem: action.embedItem }
    default:
      throw state
  }
}

export const ComposerProvider: FC<ComposerProviderType> = ({
  children,
  content,
  embeds,
  attachments,
  mentions,
  editorOptions,
  cachedMentions,
  placeholder,
  extensionConfig = DefaultExtensionConfig,
  pasteHandler,
  poll,
}) => {
  const intl = useI18n()
  const { $t } = intl
  const { document } = useDocument()
  const { asyncQuery: search } = useSearch({ fields: 'all' })
  const isTouched = useRef<boolean>(false)
  const attachmentControlRef = useRef<AttachmentPickerButtonRef>()
  const [state, dispatch] = useReducer(reducer, initialDialogsState)

  const toggleDialog: ComposerContextType['toggleDialog'] = useCallback(
    (key, value) => {
      dispatch({ type: ReducerActionKind.SET, key, value })
    },
    [],
  )

  const onSlashMenuItemClick: ComposerContextType['onSlashMenuItemClick'] =
    useCallback(
      ({ editor, props, range }) => {
        let _range = range
        if (!_range) {
          _range = {
            from: editor.view.state.selection.from,
            to: editor.view.state.selection.to,
          }
        }
        slashMenuCommand({ props, editor, range: _range })

        switch (props.type) {
          case MenuTypes.Giphy:
            toggleDialog('giphy', true)
            break
          case MenuTypes.Video:
            toggleDialog('video', true)
            break
          case MenuTypes.Audio:
            toggleDialog('audio', true)
            break
          case MenuTypes.Embed:
            toggleDialog('embed', true)
            dispatch({
              type: ReducerActionKind.SET_EMBED_ITEM,
              embedItem: props,
            })
            break
          case MenuTypes.Link:
            toggleDialog('link', true)
            break
          case MenuTypes.Attachment:
            attachmentControlRef.current?.open()
            break
          case MenuTypes.Image:
            toggleDialog('image', true)
            break
          default:
        }
      },
      [toggleDialog],
    )

  const { handlePaste: defaultPasteHandler } = useHandlePaste()
  const getItems = useStaticIntl(slashMenuItemsFactory)

  const extensions: Extensions = [
    StarterKit.configure({
      heading: false,
      codeBlock: false,
    }),
    CustomLinkExtension.configure({
      openOnClick: false,
    }),
    Underline,
    Placeholder.configure({
      placeholder:
        placeholder ||
        $t({
          id: 'Composer.ComposerProvider.DefaultPlaceholderText',
          defaultMessage: 'What are your thoughts?',
        }),
    }),
    SlashMenuExtension.configure({
      command: onSlashMenuItemClick,
      suggestion: {
        items: async ({ query }) => {
          const slashMenuItems = getItems(extensionConfig, {
            canCreatePoll: poll?.canCreate,
          })
          if (!query || query.length <= 1) {
            return slashMenuItems
          }

          return slashMenuItems.filter(item =>
            item?.label?.toLowerCase().includes(query.toLowerCase()),
          )
        },
      },
    }),
    extensionConfig.heading
      ? CustomHeadingExtension.configure({
          levels: [2, 3],
        })
      : undefined,
    extensionConfig.embed
      ? EmbedExtension.configure({
          embeds,
        })
      : undefined,
    extensionConfig.attachment
      ? AttachmentExtension.configure({
          attachments,
        })
      : undefined,
    extensionConfig.image ? AsyncImageExtension : undefined,
    extensionConfig.emoji ? EmojiExtension : undefined,
    extensionConfig.video ? VideoExtension : undefined,
    extensionConfig.code ? CodeBlockExtension : undefined,
    extensionConfig.mention
      ? MentionExtension.configure({
          HTMLAttributes: {
            class: 'text-interactive hover:text-interactive-hovered',
          },
          mentions,
          suggestion: mention({
            document,
            cachedMentions,
            search: ({ query: dirtyQuery }) => {
              const query = cleanSearchQuery(dirtyQuery)

              if (!query) {
                return new Promise(res => res(undefined))
              }

              return search({
                input: {
                  query: `${query} +for:member`,
                },
              }).then(result => {
                const members = result?.hits.find(
                  hit => hit.entityType === SearchEntityType.member,
                )

                return members?.hits?.map(m => ({
                  id: m.entityId,
                  title: m.title,
                  icon: (m?.media as Image)?.url,
                }))
              })
            },
          }),
        })
      : undefined,
    ...(extensionConfig.table
      ? [
          Table.configure({
            resizable: true,
            lastColumnResizable: true,
          }),
          TableCell,
          TableHeader,
          TableRow,
        ]
      : []),
    ...(extensionConfig.tableOfContents
      ? [TableOfContentsNode, TableOfContents]
      : []),
    extensionConfig.poll && poll ? PollNode : undefined, // as we need to now the poll entityType, if it's not passed, we don't want to render the poll extension
  ].filter(Boolean)

  const { handleUpdate } = useUploadInProgressRemovedListener()

  const { onUpdate: onEditorUpdate, ...restEditorOptions } = editorOptions || {}

  const editor = useEditor({
    content,
    extensions,
    editorProps: {
      handlePaste: pasteHandler ?? defaultPasteHandler,
      attributes: {
        class: 'prose',
      },
      transformCopied(slice) {
        // remove from poll being copied
        return new Slice(
          removeNodeFromFragment(slice.content, PollNode.name),
          slice.openStart,
          slice.openEnd,
        )
      },
    },
    ...restEditorOptions,
    onUpdate: args => {
      isTouched.current = true
      onEditorUpdate?.(args)
      handleUpdate(args)
    },
  })

  const { addAttachment } = useAddAttachmentToEditor({ editor })
  const defaultHandleAttachment = async files => {
    await Promise.all(files.map(file => addAttachment(file)))
  }

  const { onFileUpload } = useHandleFiles({
    editor,
    handleAttachmentSelect: defaultHandleAttachment,
  })

  return (
    <>
      {/* We will load this conditionally soon. Have to take into account a lot of lifecycle methods. */}
      <Helmet>
        <script src="//cdn.iframe.ly/embed.js" async></script>
      </Helmet>
      <ComposerConfigContext.Provider value={{ extensions: extensionConfig }}>
        <ComposerContext.Provider
          value={{
            withProvider: true,
            isTouched: isTouched.current,
            editor,
            onFileUpload,
            onSlashMenuItemClick,
            toggleDialog,
            state,
            poll,
          }}
        >
          {children}

          <span className="hidden">
            <AttachmentPickerButton ref={attachmentControlRef} />
          </span>
        </ComposerContext.Provider>
      </ComposerConfigContext.Provider>
    </>
  )
}
